import {
  Box,
  Button,
  Checkbox,
  Field,
  HStack,
  IconButton,
  Input,
  Link,
  Popover,
  Portal,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import { useBlocksConfig } from "@pathwright/blocks-engine"
import { PRIMARY_GRAY } from "@pathwright/ui/src/components/utils/colors"
import { resolveGenericLinkUrl } from "@pathwright/utils/src/url/generic-link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useMemo, useState } from "react"
import { useGenericLinkContext } from "../../utils/urls/genericLinks"
import validateURL, { getValidationMessage } from "../../utils/urls/validateURL"

const inputGray = "rgb(136, 136, 136)"
const inputFontSize = "0.9em"

export const ButtonBlockButton = ({
  link,
  label,
  onClick,
  disabled,
  openInNewTab
}) => {
  const { theme } = useBlocksConfig()
  const genericLinkContext = useGenericLinkContext()
  const shouldOpenInNewTab = openInNewTab ?? true

  const href = useMemo(
    () => (link ? resolveGenericLinkUrl(link, genericLinkContext) : link),
    [link, genericLinkContext.cohortId, genericLinkContext.resourceId]
  )

  const isDisabled = useMemo(
    () => disabled || (!link && !onClick),
    [disabled, link, onClick]
  )

  const handleClick = (event) => {
    if (onClick || isDisabled) {
      event.preventDefault()
      event.stopPropagation()
      if (!isDisabled) onClick(event)
    }
  }

  return (
    <Button
      asChild
      bgColor={theme?.primaryBrandColor || PRIMARY_GRAY}
      _hover={{
        bgColor: theme?.primaryBrandColor || PRIMARY_GRAY,
        opacity: isDisabled ? 0.4 : 0.9
      }}
      fontSize="16px"
      textDecoration="none !important"
      padding="7px 18px"
      margin="9px 0px"
      lineHeight="1.4"
      cursor={!isDisabled ? "pointer !important" : undefined}
      fontFamily="body"
      color="white !important"
      disabled={isDisabled}
      onClick={handleClick}
    >
      <Link
        target={shouldOpenInNewTab ? "_blank" : undefined}
        rel={shouldOpenInNewTab ? "noopener noreferrer" : undefined}
        href={href}
      >
        {label || "Join Meeting"}
      </Link>
    </Button>
  )
}

export const ButtonEditButton = ({
  onChange,
  buttonLabel: propsButtonLabel,
  link: propsLink,
  openInNewTab: propsOpenInNewTab
}) => {
  const { open, onOpen, onClose, onToggle } = useDisclosure()
  const initialOpenInNewTab = propsOpenInNewTab ?? true

  const [buttonLabel, setButtonLabel] = useState(propsButtonLabel)
  const [link, setButtonLink] = useState(propsLink)
  const [openInNewTab, setOpenInNewTab] = useState(initialOpenInNewTab)
  const [error, setError] = useState("")

  const handleClose = () => {
    setButtonLabel(propsButtonLabel)
    setButtonLink(propsLink)
    setOpenInNewTab(initialOpenInNewTab)
    onClose()
  }

  const handleChangeButtonLabel = (nextButtonLabel) =>
    setButtonLabel(nextButtonLabel)

  const handleChangeLink = (nextLink) => {
    // Uncheck "Open in new tab" if it has never been set, and the link is a relative path
    if (
      openInNewTab === undefined &&
      nextLink &&
      nextLink.trim().startsWith("/")
    ) {
      setOpenInNewTab(false)
    }

    setButtonLink(nextLink.trim())
    setError("")
  }

  const handleSubmit = () => {
    onChange({ buttonLabel, link: link.trim(), openInNewTab })
    onClose()
  }

  const linkValidation = useMemo(() => validateURL(link), [link])
  const linkIsValid = useMemo(() => linkValidation === true, [linkValidation])

  const validateLink = () => {
    if (!linkIsValid) {
      // Automatically supply https:// if no protocol
      if (linkValidation === "missingProtocol") {
        setButtonLink(`https://${link}`)
        return
      }
      const message = getValidationMessage(linkValidation)
      setError(message)
    } else {
      setError("")
    }
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(e) => (e.open ? onOpen() : handleClose())}
    >
      <Popover.Trigger asChild onClick={onToggle}>
        <HStack w="max-content" pos="relative" justify="center">
          <ButtonBlockButton
            label={buttonLabel}
            link={link}
            onClick={onOpen}
            disabled={!linkIsValid}
            openInNewTab={openInNewTab}
          />
          <Box position="absolute" right="-40px">
            <IconButton
              children={<Pathicon icon="edit" size="1em" />}
              aria-label="Edit button"
              bg="rgb(239, 239, 239)"
              _hover={{
                bg: "rgb(239, 239, 239)",
                opacity: 0.9
              }}
              cursor="pointer"
              color="rgb(136, 134, 135)"
              height="30px"
              width="30px"
              minW="30px"
            />
          </Box>
        </HStack>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content bg="white" borderRadius="md">
            <Popover.Arrow>
              <Popover.ArrowTip borderColor="gray.200" />
            </Popover.Arrow>
            <Popover.Body>
              <VStack gap={5}>
                <Field.Root w="full" color={inputGray} fontSize={inputFontSize}>
                  <Field.Label w="full">Link</Field.Label>
                  <Input
                    border="1px solid rgba(0,0,0,0.2)"
                    marginTop="0.5rem"
                    placeholder="https://www.pathwright.com"
                    value={link}
                    borderRadius="4px"
                    p="5px"
                    onChange={(e) => handleChangeLink(e.target.value)}
                    onBlur={validateLink}
                    color={PRIMARY_GRAY}
                    _placeholder={{ color: "rgba(0,0,0,0.2)" }}
                  />
                  <Field.ErrorText>{error}</Field.ErrorText>
                </Field.Root>
                <Field.Root w="full" color={inputGray} fontSize={inputFontSize}>
                  <Field.Label w="full">Label</Field.Label>
                  <Input
                    border="1px solid rgba(0,0,0,0.2)"
                    marginTop="0.5rem"
                    placeholder="Label"
                    value={buttonLabel}
                    borderRadius="4px"
                    p="5px"
                    onChange={(e) => handleChangeButtonLabel(e.target.value)}
                    color={PRIMARY_GRAY}
                    _placeholder={{ color: "rgba(0,0,0,0.2)" }}
                  />
                </Field.Root>
                <Checkbox.Root
                  checked={openInNewTab}
                  onCheckedChange={(e) => setOpenInNewTab(!!e.checked)}
                  alignSelf="start"
                  cursor="pointer"
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control cursor="pointer">
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                  <Checkbox.Label>Open in new tab</Checkbox.Label>
                </Checkbox.Root>
                <Button
                  onClick={handleSubmit}
                  w="full"
                  borderRadius="md"
                  bg={PRIMARY_GRAY}
                  color="white"
                  _hover={{ bg: PRIMARY_GRAY }}
                  disabled={
                    !linkIsValid ||
                    (link === propsLink &&
                      buttonLabel === propsButtonLabel &&
                      openInNewTab === propsOpenInNewTab)
                  }
                >
                  Save
                </Button>
                <Link variant="underline" onClick={handleClose}>
                  Or cancel
                </Link>
              </VStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}
